import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, MessageCircle, FileText, Users, Lightbulb, ArrowLeft, Workflow, Mail, HelpCircle, AlertCircle, Target, Zap, Heart, Clock } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const MarketingInterviewProcessSection = ({ onBack, onNext, nextSectionTitle, hasNext, onComplete, isCompleted }) => {

  const interviewStages = [
    {
      Icon: MessageCircle,
      title: '1. Intro interview (30-45 mins)',
      description: 'Usually with the founder, Head of Marketing or first marketing hire. Focuses on your background, motivations and how you think.',
      keyPoints: [
        '"Why this company and role?"',
        '"Tell me about yourself"',
        '"Why startups?"',
        '"What channels have you worked on and what did you learn?"',
        '"How do you measure success in your marketing work?"',
        '"What\'s a campaign you\'re proud of?"',
      ],
      pointsTitle: 'You may be asked:',
      additionalInfo: 'Come ready to speak in both creative and commercial terms – they want to hear how you think and what results you drove.'
    },
    {
      Icon: FileText,
      title: '2. Case study / take-home task',
      description: 'Usually a task related to campaign ideation, copywriting, brand strategy or user acquisition.',
      keyPoints: [
        'Can include writing mock emails, improving a landing page or proposing a content calendar',
        'You\'ll likely present or discuss your work in a follow-up call',
      ],
    },
    {
      Icon: Users,
      title: '3. Final round interview',
      description: 'Tests how well you collaborate, respond to feedback and align with the company\'s mission.',
      keyPoints: [
        'Often includes culture chats or deeper dives into your thinking',
        'May involve a founder, team members or cross-functional teammates (e.g. Product or Sales)',
      ],
    },
  ];

  const startupQualities = [
    {
      icon: Workflow,
      title: "Adaptability",
      description: "Can you stay calm and solve problems when things change fast?",
      type: "success"
    },
    {
      icon: Target,
      title: "Ownership", 
      description: "Do you take responsibility without being chased?",
      type: "success"
    },
    {
      icon: Zap,
      title: "Initiative",
      description: "Are you the kind of person who gets started rather than waiting to be told what to do?",
      type: "success"
    },
    {
      icon: Heart,
      title: "Comfort with ambiguity",
      description: "Can you figure things out when the path isn't clear?",
      type: "success"
    }
  ];

  const etiquetteTips = [
    {
      title: "Reply to startups within 24 hours",
      description: "Keep momentum – this shows reliability"
    },
    {
      title: "Follow up if a startup hasn't replied in 48-72 hours",
      description: "Use polite chaser emails (e.g. \"Just checking in to find a time that works for our next call...\")"
    },
    {
      title: "Always send a thank-you email after interviews",
      description: "Reiterate your interest, reference something from the chat and confirm next steps"
    },
    {
      title: "Reschedule respectfully",
      description: "If needed, give at least 4 hours' notice"
    },
    {
      title: "Communicate with Jumpstart",
      description: "Let Bethan (bethan@jumpstart-uk.com) know if you've received an offer. For all other queries, email info@jumpstart-uk.com and we'll get back to you ASAP"
    }
  ];

  const commonQuestions = [
    "Why this company and why now?",
    "Tell me about a time you solved something with limited information",
    "How do you prioritise when everything feels urgent?",
    "What motivates you?",
    "When have you taken initiative outside your job description?",
    "Tell me about a time you failed or received tough feedback",
    "How do you come up with new ideas?",
    "How do you balance creativity with performance?",
    "What's your approach to analysing what's working in a channel?"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Marketing interviews at startups: the essentials
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Whilst every company is a bit different, here's the common flow and what early-stage startups really care about.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="📝 How startup interviews typically work">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Most early-stage startups follow a similar interview flow for marketing roles:
              </p>
              
              <div className="space-y-8">
                {interviewStages.map((stage, index) => (
                  <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#02B491] rounded-xl flex items-center justify-center">
                        <stage.Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{stage.title}</h3>
                        <p className="text-slate-700 leading-relaxed">{stage.description}</p>
                      </div>
                    </div>
                    
                    {stage.keyPoints && (
                      <div className="ml-16">
                        <h4 className="font-semibold text-gray-900 mb-3">{stage.pointsTitle || 'Key points:'}</h4>
                        <div className="space-y-2">
                          {stage.keyPoints.map((point, pointIndex) => (
                            <div key={pointIndex} className="flex items-start space-x-3">
                              <div className="flex-shrink-0 w-2 h-2 bg-[#02B491] rounded-full mt-2"></div>
                              <p className="text-slate-600 leading-relaxed">{point}</p>
                            </div>
                          ))}
                        </div>
                        {stage.additionalInfo && (
                          <p className="text-slate-700 font-medium mt-4 p-4 bg-emerald-50 rounded-xl">
                            {stage.additionalInfo}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="⭐ What early-stage startups really care about">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                These qualities are often more important than years of experience:
              </p>
              
              <div className="grid gap-6 md:grid-cols-2">
                {startupQualities.map((quality, index) => (
                  <InfoCard
                    key={index}
                    icon={quality.icon}
                    title={quality.title}
                    description={quality.description}
                    type={quality.type}
                  />
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="🤝 General interview etiquette & tips">
            <div className="space-y-8">
              <div className="space-y-6">
                {etiquetteTips.map((tip, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#02B491] rounded-xl flex items-center justify-center mt-1">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{tip.title}</h4>
                      <p className="text-slate-600 leading-relaxed">{tip.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="❓ Common marketing interview questions">
            <div className="space-y-8">
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="grid gap-4 md:grid-cols-2">
                  {commonQuestions.map((question, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-[#02B491] rounded-full mt-2"></div>
                      <p className="text-slate-700 leading-relaxed">{question}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="🆘 What to do if you're not getting traction">
            <div className="space-y-8">
              <InfoCard
                icon={AlertCircle}
                title="Need help with your applications?"
                description="If you've applied to lots of roles but have had very few or no interviews, email info@jumpstart-uk.com. We can help review your profile and applications, suggest small changes that could improve traction, and advise whether to change tack re: the roles you're applying for."
                type="warning"
              />
              
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <p className="text-amber-800 text-sm font-medium">
                  💡 Only reach out if you're genuinely struggling to land interviews because capacity is limited!
                </p>
              </div>
            </div>
          </StepCard>

        </div>
      </div>

        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default MarketingInterviewProcessSection; 